import { toast } from "@/components/common/Toast/Toast"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useEffect, useRef, useState } from "react"
import type { ConditionFieldRow } from "../../../../../api/types"
import type { WorklistIssue } from "../../studio/cellState"
import type { FrameworkState } from "../loadFrameworkState"
import type { FrameworkDerivation } from "../mapping/deriveFrameworkVerdict"
import type { StatementCard } from "../useStudioV2State"
import { useStudioV2Url } from "../useStudioV2Url"
import { useConformFramework } from "./useConformFramework"

export interface BulkConformGestureProps {
  declId: string
  frameworks: string[]
  derivationOf: (framework: string) => FrameworkDerivation | null
  openByFramework: Map<string, WorklistIssue[]>
  statementCardsByFramework: Map<string, StatementCard[]>
  /** Resolves a framework slug to its display name — the partial-failure
   *  toast names WHICH Verordnungen failed, not just how many. Defaults to
   *  the identity function so callers/tests with no name source still
   *  compile (the slug renders in place of a name). */
  nameOf?: (framework: string) => string
  /** Cells (`fw:articleNumber`) whose published condition finding is SOFTENED —
   *  `StateContext["conditionFindingUnconfirmed"]`, straight from the flow.
   *  Optional: a caller with no flow context passes nothing and the gesture
   *  behaves exactly as it did before this existed. */
  softenedCells?: ReadonlySet<string>
  /** STEP C3-b3: set in the paged review, where only the open regulation is loaded.
   *  Each regulation the sweep acts on is loaded first (`loadFrameworkState`), and its
   *  OWN issues, cards and softened cells replace the props above — never the open
   *  regulation's. `openByFramework` then only decides which regulations have open
   *  work at all (the server's counts). */
  loadState?: (framework: string) => Promise<FrameworkState>
  /** Paged: called once a run is over (it wrote to regulations other than the open
   *  one, so any copy of them kept for display is out of date). */
  onRunSettled?: () => void
}

/**
 * ELIGIBLE FOR THE BULK GESTURE: R4-compliant (the base verdict itself, not
 * "effectively compliant" — a framework whose ADVERSE units sit outside our
 * purchase still gets a green head pill, but its open work, if any, is
 * whatever produced the "effectively" qualifier and deserves the reviewer's
 * own look) AND every one of its open issues is a plain yellow sign-off
 * (`needs-verification` on a STATEMENT unit) — never `needs-input` (missing
 * data), never `ready` (awaits the studio's own Continue), never a framework-
 * unit edition conflict.
 *
 * ...AND NEVER A SOFTENED CONDITION FINDING (TUR-1211). Such a cell is a plain
 * `needs-verification` to the worklist, so it would otherwise sweep through
 * here, but it is not a routine sign-off: it is an unresolved CONTRADICTION
 * between M5's conditional reading of the article and the statements' own
 * non-conditional one, and the reviewer is the only one who can say which is
 * right. One click over the whole declaration must not answer that for them.
 * The per-framework gesture still signs it in one press — the reviewer standing
 * on that Verordnung has seen it.
 */
function derivationEligible(derivation: FrameworkDerivation | null): boolean {
  return derivation?.rule === "R4" && derivation.verdict === "compliant"
}

function eligible(
  derivation: FrameworkDerivation | null,
  issues: readonly WorklistIssue[],
  softenedCells: ReadonlySet<string>,
): boolean {
  if (!derivationEligible(derivation)) return false
  if (
    issues.some((i) =>
      i.articleIds.some((a) => softenedCells.has(`${i.framework}:${a}`)),
    )
  )
    return false
  return issues.every(
    (i) => i.unit === "statement" && i.state === "needs-verification",
  )
}

/**
 * Navigator footer — "+ alle konformen als geprüft markieren". Runs the same
 * bundling sequence (`useConformFramework`) over every eligible framework,
 * skipping the rest WITHOUT a per-framework toast (`useConformFramework`
 * already names a blocked statement when it stops mid-sequence; a framework
 * this gesture never attempts has nothing to name) — one summary toast at
 * the end names marked/failed/skipped, and the URL's active framework
 * (`useStudioV2Url`, called directly here — it reads the live search params
 * itself, so this needs no wiring through `StudioV2Layout`) advances to the
 * first Verordnung still open after the run, the prototype's own behaviour.
 */
export function BulkConformGesture({
  declId,
  frameworks,
  derivationOf,
  openByFramework,
  statementCardsByFramework,
  nameOf = (fw) => fw,
  softenedCells = new Set<string>(),
  loadState,
  onRunSettled,
}: BulkConformGestureProps) {
  const { translate } = useTranslation()
  const { conformFramework, isRunning } = useConformFramework(declId)
  const url = useStudioV2Url()
  const [busy, setBusy] = useState(false)
  // The regulation being loaded right now (paged), shown on the button.
  const [loadingName, setLoadingName] = useState<string | null>(null)
  // Paged only: a run outlives nothing. Once the gesture unmounts (the reviewer left
  // the declaration, or the navigator went away), the loop stops before its next load
  // or write and says how far it got. Flag off finishes the run as it always has.
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const candidates = frameworks.filter(
    (fw) => (openByFramework.get(fw)?.length ?? 0) > 0,
  )

  const run = async () => {
    setBusy(true)
    let marked = 0
    // Frameworks this run actually flipped to geprüft, in `candidates`
    // order — the complement (still in `candidates`, not in here) is what
    // "still open after the run" means below, without waiting for the
    // parent's props to catch up to the mutations just fired.
    const markedFrameworks = new Set<string>()
    // A framework whose mutation actually THREW (a real save failure —
    // `conformFramework` already toasted the specific reason) — tracked
    // SEPARATELY from a framework that was never attempted at all
    // (ineligible) or that saved what it could but left article-scope work
    // standing (`openScopeStatements > 0`, a legitimate partial skip, not an
    // error). Conflating the two used to report a genuine save failure with
    // the same blanket `toast.success` as an ordinary skip.
    let failed = 0
    // Display names of the frameworks counted in `failed` above, in the
    // order they failed — the partial-failure toast names WHICH Verordnungen
    // need a manual look, not just how many.
    const failedNames: string[] = []
    // ONE shared row set spans the WHOLE run (`useConformFramework`'s own
    // doc): every call in this loop lands inside the same continuous async
    // body with no re-render between them, so a call re-reading the query's
    // pre-run snapshot instead of the PREVIOUS call's returned `rows` would
    // silently drop that earlier framework's just-written `__verified__`
    // mark on the next wholesale-replace PUT.
    let rows: ConditionFieldRow[] | undefined
    // Paged: regulations that could not be LOADED — nothing was written for them,
    // which is not the same as a save that failed, so they are named on their own.
    const unloadedNames: string[] = []
    let stopped = false
    for (const fw of candidates) {
      if (loadState && !mounted.current) {
        stopped = true
        break
      }
      let issues = openByFramework.get(fw) ?? []
      let cards = statementCardsByFramework.get(fw) ?? []
      let softened = softenedCells
      let derivation = derivationOf(fw)
      // Paged: each regulation's rows are its own slice's (no shared table to carry).
      let startingRows = rows
      if (loadState) {
        // The summary's derivation only spares a download: a regulation it already
        // rules out is never loaded. The DECISION is taken on the regulation's own
        // fresh data below — another session may have changed it since the summary.
        if (!derivationEligible(derivation)) continue
        setLoadingName(nameOf(fw))
        let state: FrameworkState
        try {
          state = await loadState(fw)
        } catch {
          if (!mounted.current) {
            stopped = true
            break
          }
          setLoadingName(null)
          unloadedNames.push(nameOf(fw))
          continue
        }
        if (!mounted.current) {
          stopped = true
          break
        }
        setLoadingName(null)
        issues = state.issues
        cards = state.cards
        softened = state.softenedCells
        derivation = state.derivation
        startingRows = state.slice.conditionRows
        // Nothing left to sign on its fresh data: a skip, never counted as marked.
        if (issues.length === 0) continue
      }
      if (!eligible(derivation, issues, softened)) continue
      const result = await conformFramework(fw, cards, issues, startingRows)
      if (!loadState) rows = result.rows
      // "Marked" means the framework can actually flip to geprüft — a run
      // that left article-scope work standing (openScopeStatements) saved
      // its part but did NOT check the framework, and counting it here would
      // announce a number the navigator visibly contradicts.
      if (result.ok && result.openScopeStatements === 0) {
        marked++
        markedFrameworks.add(fw)
      } else if (!result.ok) {
        failed++
        failedNames.push(nameOf(fw))
      }
      // `result.ok && openScopeStatements > 0` falls through uncounted here —
      // a legitimate partial skip, the same bucket as an ineligible framework.
    }
    if (loadState) onRunSettled?.()
    if (stopped) {
      // The reviewer left mid-run: no state to set, but say how far it got.
      toast.warning(translate("MC_DECL_V2_BULK_CONFORM_STOPPED", { n: marked }))
      return
    }
    setBusy(false)
    // THE PROTOTYPE'S OWN BEHAVIOUR: land the reviewer on the first Verordnung
    // that still needs them, rather than leaving the navigator focused on one
    // that just got checked off (or was never open at all). Computed from
    // THIS run's own bookkeeping — `candidates` minus `markedFrameworks` —
    // not from `openByFramework`, which is a snapshot from BEFORE the
    // mutations above and has not caught up yet.
    const firstStillOpen = candidates.find((fw) => !markedFrameworks.has(fw))
    if (firstStillOpen) {
      url.patch({ framework: firstStillOpen })
    }
    if (candidates.length === 0) {
      toast.success(translate("MC_DECL_V2_BULK_CONFORM_NONE"))
      return
    }
    const skipped = candidates.length - marked - failed - unloadedNames.length
    if (unloadedNames.length > 0) {
      // Paged: nothing was written for these — they could not be read. Their own
      // wording, so a reviewer does not go looking for a half-saved regulation.
      toast.warning(
        translate("MC_DECL_V2_BULK_CONFORM_LOAD_PARTIAL", {
          n: marked,
          u: unloadedNames.length,
          names: unloadedNames.join(", "),
        }),
      )
      if (failed === 0) return
    }
    if (failed > 0) {
      // A real save failure happened — this must never read as plain
      // success, and it must name failed separately from skipped so the
      // reviewer knows which frameworks to go check by hand.
      toast.warning(
        translate("MC_DECL_V2_BULK_CONFORM_PARTIAL", {
          n: marked,
          f: failed,
          m: skipped,
          names: failedNames.join(", "),
        }),
      )
      return
    }
    toast.success(
      translate("MC_DECL_V2_BULK_CONFORM_SUMMARY", {
        n: marked,
        m: skipped,
      }),
    )
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* The narrow navigator ellipsizes this label (`.sv2-fwall`) — the
              tooltip explains what the gesture actually does (forward-
              looking), rather than just repeating the (already ellipsized)
              button text. Wrapped in a span, not put directly on the
              button, because a DISABLED button does not reliably dispatch
              the hover events a tooltip trigger needs while running. */}
          <span>
            <button
              type="button"
              disabled={busy || isRunning}
              onClick={run}
              className="sv2-fwall"
              aria-busy={loadingName !== null || undefined}
            >
              {loadingName !== null
                ? translate("MC_DECL_V2_BULK_CONFORM_LOADING", {
                    name: loadingName,
                  })
                : translate("MC_DECL_V2_BULK_CONFORM_BUTTON")}
            </button>
          </span>
        </TooltipTrigger>
        {/* Paged: the regulation being loaded, announced to screen readers (the
            button's own label changes too, but a label change is not announced). */}
        {loadState && (
          <span className="sr-only" role="status" aria-live="polite">
            {loadingName !== null
              ? translate("MC_DECL_V2_BULK_CONFORM_LOADING", {
                  name: loadingName,
                })
              : ""}
          </span>
        )}
        <TooltipContent side="top">
          {translate("MC_DECL_V2_BULK_CONFORM_TITLE")}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
